import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  FileText,
  Search,
  Moon,
  Sun,
  Menu,
  User,
  LogOut,
  X,
  Loader2,
  ShoppingCart,
  CheckSquare,
  History,
  Filter,
  Check,
  Users
} from "lucide-react";
import { cn } from "../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Definim noile domenii de căutare
const SEARCH_SCOPES = [
  { id: "personal-notes", label: "My Notes", icon: FileText },
  { id: "common-notes", label: "Common Notes", icon: Users },
  { id: "personal-todos", label: "My Tasks", icon: CheckSquare },
  { id: "common-todos", label: "Common Tasks", icon: CheckSquare },
  { id: "orders", label: "Orders", icon: ShoppingCart },
];

export const TopBar = ({
  user,
  searchQuery,
  onSearch,
  searchResults,
  isSearching,
  onSelectSearchResult,
  sidebarOpen,
  onToggleSidebar,
  showSidebarToggle = true,
}) => {
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [searchFocused, setSearchFocused] = useState(false);
  
  // NOU: Un Array cu ce este bifat, implicit bifăm tot
  const [searchScopes, setSearchScopes] = useState([
    "personal-notes", "common-notes", "personal-todos", "common-todos", "orders"
  ]);
  
  const [advancedResults, setAdvancedResults] = useState({ 
    personal_notes: [], common_notes: [], personal_todos: [], common_todos: [], orders: [] 
  });
  const [isAdvancedSearching, setIsAdvancedSearching] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const searchRef = useRef(null);
  const searchDebounceRef = useRef(null);

  const [searchHistory, setSearchHistory] = useState(() => {
    const saved = localStorage.getItem("notekeeper_search_history");
    return saved ? JSON.parse(saved) : [];
  });

  const saveToHistory = (query) => {
    if (!query || !query.trim()) return;
    const term = query.trim();
    setSearchHistory(prev => {
      const newHistory = [term, ...prev.filter(q => q !== term)].slice(0, 10);
      localStorage.setItem("notekeeper_search_history", JSON.stringify(newHistory));
      return newHistory;
    });
  };

  // NOU: Funcție pentru a bifa/debifa module
  const handleScopeToggle = (scopeId) => {
    setSearchScopes(prev => {
      const isSelected = prev.includes(scopeId);
      let newScopes;
      
      if (isSelected) {
        if (prev.length === 1) return prev; // Nu lăsăm utilizatorul să debifeze absolut tot
        newScopes = prev.filter(id => id !== scopeId);
      } else {
        newScopes = [...prev, scopeId];
      }
      
      // Relansăm căutarea cu noile bife, dacă avem deja un cuvânt tastat
      if (localSearchQuery) {
        performAdvancedSearch(localSearchQuery, newScopes);
      }
      return newScopes;
    });
  };

  const performAdvancedSearch = useCallback(async (query, scopesToUse) => {
    if (!query.trim()) {
      setAdvancedResults({ personal_notes: [], common_notes: [], personal_todos: [], common_todos: [], orders: [] });
      return;
    }

    setIsAdvancedSearching(true);
    try {
      const response = await axios.post(`${API_URL}/search/advanced`, {
        query,
        scopes: scopesToUse // Trimitem array-ul cu bife către Backend
      });
      setAdvancedResults(response.data);
    } catch (error) {
      console.error("Advanced search failed:", error);
    } finally {
      setIsAdvancedSearching(false);
    }
  }, []);

  const handleSearchInput = (value) => {
    setLocalSearchQuery(value);
    if (onSearch) {
      onSearch(value);
    }
    
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    
    searchDebounceRef.current = setTimeout(() => {
      performAdvancedSearch(value, searchScopes);
    }, 300);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const totalResults = 
    advancedResults.personal_notes.length + 
    advancedResults.common_notes.length + 
    advancedResults.orders.length + 
    advancedResults.personal_todos.length + 
    advancedResults.common_todos.length;

  const currentSearchQuery = searchQuery || localSearchQuery;

  // Funcție reutilizabilă pentru a randa secțiunile de Notes (atât Personal cât și Common)
  const renderNotesSection = (notesArray, title, icon) => {
    if (!notesArray || notesArray.length === 0) return null;
    
    const grouped = Object.values(notesArray.reduce((acc, result) => {
      const key = result.path || result.name;
      if (!acc[key]) acc[key] = { name: result.name, path: result.path, items: [] };
      acc[key].items.push(result);
      return acc;
    }, {}));

    return (
      <div>
        <div className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase bg-muted/30 flex items-center gap-2">
          {icon} {title} ({notesArray.length})
        </div>
        {grouped.slice(0, 5).map((group, groupIndex) => (
          <div key={`${title}-group-${groupIndex}`} className="border-b border-border last:border-b-0">
            <div className="px-4 py-2 bg-muted/20 flex items-center gap-2 border-b border-border/50">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium text-sm text-foreground">{group.name}</span>
            </div>
            {group.items.map((result, itemIndex) => (
              <button
                key={`${title}-item-${groupIndex}-${itemIndex}`}
                onClick={() => {
                  saveToHistory(currentSearchQuery);
                  onSelectSearchResult({ 
                    ...result, 
                    query: currentSearchQuery,
                    matchIndex: itemIndex 
                  });
                  setSearchFocused(false);
                }}
                className="w-full px-4 py-2 text-left hover:bg-muted/50 transition-colors flex items-center gap-3 pl-8"
              >
                <span className="text-xs font-mono text-muted-foreground w-12 shrink-0 text-right">
                  Ln {result.line_number}
                </span>
                <div className="w-px h-4 bg-border shrink-0" />
                <p className="text-sm text-foreground truncate flex-1">
                  {result.snippet}
                </p>
              </button>
            ))}
          </div>
        ))}
      </div>
    );
  };

  return (
    <header className="h-12 md:h-14 border-b border-border bg-background/80 backdrop-blur-xl flex items-center px-2 md:px-4 gap-2 md:gap-4 sticky top-0 z-50" data-testid="top-bar">
      <div className="flex items-center gap-1 md:gap-3">
        {showSidebarToggle && (
          <Button
            id="mobile-menu-btn"
            variant="ghost"
            size="icon"
            onClick={onToggleSidebar}
            className="h-8 w-8"
            data-testid="toggle-sidebar-button"
          >
            <Menu className="w-4 h-4" />
          </Button>
        )}
        
        <div className="flex items-center gap-1 md:gap-2">
          <div className="w-6 h-6 md:w-8 md:h-8 rounded-lg bg-primary flex items-center justify-center">
            <FileText className="w-3 h-3 md:w-4 md:h-4 text-primary-foreground" />
          </div>
          <span className="text-base md:text-lg font-semibold tracking-tight hidden sm:inline">NoteKeeper</span>
        </div>
      </div>

      <div className="flex-1 max-w-xl mx-auto relative" ref={searchRef}>
        <div className="relative flex items-center gap-1">
          {/* NOU: Meniul cu bife pentru filtrare avansată */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-[45px] sm:w-[50px] h-9 bg-muted/50 border-r-0 rounded-r-none px-0" data-testid="search-filter-button">
                <Filter className="w-4 h-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56 z-[60]">
              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase">
                Caută în...
              </div>
              <DropdownMenuSeparator />
              {SEARCH_SCOPES.map((scope) => {
                const isSelected = searchScopes.includes(scope.id);
                return (
                  <DropdownMenuItem
                    key={scope.id}
                    onSelect={(e) => {
                      e.preventDefault(); // Împiedică închiderea meniului când bifezi
                      handleScopeToggle(scope.id);
                    }}
                    className="flex items-center gap-3 cursor-pointer py-2"
                  >
                    <div className={cn(
                      "w-4 h-4 border rounded flex items-center justify-center shrink-0 transition-colors",
                      isSelected ? "bg-primary border-primary text-primary-foreground" : "border-input"
                    )}>
                      {isSelected && <Check className="w-3 h-3" />}
                    </div>
                    <scope.icon className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium">{scope.label}</span>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search..."
              value={currentSearchQuery}
              onChange={(e) => handleSearchInput(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              className="pl-9 pr-9 h-9 bg-muted/50 rounded-l-none"
              data-testid="search-input"
            />
            {currentSearchQuery && (
              <button
                onClick={() => {
                  setLocalSearchQuery("");
                  if (onSearch) onSearch("");
                  setAdvancedResults({ personal_notes: [], common_notes: [], personal_todos: [], common_todos: [], orders: [] });
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {searchFocused && !currentSearchQuery && searchHistory.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-popover border border-border rounded-lg shadow-lg overflow-hidden z-50 animate-fade-in">
            <div className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase bg-muted/30">
              Ultimele căutări
            </div>
            {searchHistory.map((term, index) => (
              <button
                key={`history-${index}`}
                onClick={() => {
                  setLocalSearchQuery(term);
                  if (onSearch) onSearch(term);
                  performAdvancedSearch(term, searchScopes);
                }}
                className="w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors border-b border-border last:border-b-0 flex items-center gap-3"
              >
                <History className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">{term}</span>
              </button>
            ))}
          </div>
        )}

        {searchFocused && currentSearchQuery && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-popover border border-border rounded-lg shadow-lg overflow-hidden z-50 animate-fade-in">
            {isAdvancedSearching ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : totalResults === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No results found
              </div>
            ) : (
              <div className="max-h-80 overflow-y-auto">
                {renderNotesSection(advancedResults.personal_notes, "Personal Notes", <FileText className="w-3 h-3" />)}
                {renderNotesSection(advancedResults.common_notes, "Common Notes", <Users className="w-3 h-3" />)}

                {advancedResults.orders.length > 0 && (
                  <div>
                    <div className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase bg-muted/30 flex items-center gap-2">
                      <ShoppingCart className="w-3 h-3" /> Orders ({advancedResults.orders.length})
                    </div>
                    {advancedResults.orders.slice(0, 5).map((result, index) => (
                      <button
                        key={`order-${index}`}
                        onClick={() => {
                          saveToHistory(currentSearchQuery);
                          onSelectSearchResult({ ...result, type: "order" });
                          setSearchFocused(false);
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors border-b border-border last:border-b-0"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <ShoppingCart className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium text-sm">{result.name}</span>
                          <span className="text-xs px-2 py-0.5 rounded bg-muted">{result.status}</span>
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{result.snippet}</p>
                      </button>
                    ))}
                  </div>
                )}

                {(advancedResults.personal_todos.length > 0 || advancedResults.common_todos.length > 0) && (
                  <div>
                    <div className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase bg-muted/30 flex items-center gap-2">
                      <CheckSquare className="w-3 h-3" /> Tasks
                    </div>
                    {[...advancedResults.personal_todos, ...advancedResults.common_todos].slice(0, 5).map((result, index) => (
                      <button
                        key={`todo-${index}`}
                        onClick={() => {
                          saveToHistory(currentSearchQuery);
                          onSelectSearchResult({ ...result, type: result.type });
                          setSearchFocused(false);
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors border-b border-border last:border-b-0"
                      >
                        <div className="flex items-center gap-2">
                          <CheckSquare className={cn("w-4 h-4", result.completed ? "text-green-500" : "text-muted-foreground")} />
                          <span className={cn("font-medium text-sm flex-1", result.completed && "line-through text-muted-foreground")}>
                            {result.type === "common-todo" ? <Users className="inline w-3 h-3 mr-1 text-blue-500" /> : null}
                            {result.text}
                          </span>
                          <span className={cn(
                            "text-xs px-2 py-0.5 rounded",
                            result.category === "important" && "bg-red-500/10 text-red-500",
                            result.category === "waiting" && "bg-yellow-500/10 text-yellow-500",
                            result.category === "paused" && "bg-muted text-muted-foreground",
                            result.category === "normal" && "bg-muted"
                          )}>
                            {result.category}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="h-8 w-8"
          data-testid="theme-toggle-button"
        >
          {theme === "dark" ? (
            <Sun className="w-4 h-4" />
          ) : (
            <Moon className="w-4 h-4" />
          )}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="user-menu-button">
              <User className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{user?.username}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="logout-button">
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};